/* ==========================================================================
   ADD TO CART BUTTON

   Two jobs: keep the price inside the button honest, and include whatever
   add-ons are switched on.

   WHY IT UPDATES AT ALL. Measured 29 Aug 2026 against assets/product-info.js:
   on a variant change Dawn does NOT refetch or swap the buy-buttons markup. It
   swaps five ids only (price, Sku, Inventory, Volume, Price-Per-Item) and then
   calls toggleSubmitButton, which writes textContent into the button's FIRST
   span. So a server-rendered price inside this button is never updated by Dawn
   and would silently go stale on the second variant a customer looks at, which
   is the worst kind of wrong: quietly, and about money.

   WHERE THE NUMBER COMES FROM. The theme's own price element, not a number
   formatted here. product-info.js has already refreshed #price-<section> by the
   time variantChange is published, so reading it back gives the exact string
   the rest of the page shows, with the shop's money format, its currency code
   setting and its market pricing already applied.

   ADDING THE ADD-ONS. Thomas, 29 Aug: "when i'm toggling the add on the price
   in the 'add to cart' should change to include the price of the squeegee". He
   is right; the button states what the click costs, and clicking it with the
   squeegee on puts more than the product price into the cart.

   That needs arithmetic, and arithmetic needs a number, not a string. So the
   totals are summed in minor units and then RE-RENDERED IN THE PAGE'S OWN
   SHAPE: the base price is known both as a string and in minor units, so the
   separator style and decimal count are derived by finding which formatting
   reproduces the string exactly, and only then is the total written the same
   way. If no candidate reproduces it, the button keeps the untouched base
   price rather than inventing a format. It can be right or it can decline; it
   cannot be wrong.

   THE STRUCK PRICE HAS TO MOVE TOO. Otherwise switching a 95p add-on onto a
   £4.05 product shows "£5.00 £4.50", which reads as a price rise. The compare
   at total includes the add-on's own compare-at, and is shown only while it is
   genuinely higher than the total being charged.
   ========================================================================== */

(function () {
  'use strict';

  if (window.__stkBtnBound) return;
  window.__stkBtnBound = true;

  /* Dawn declares PUB_SUB_EVENTS as a top-level `const` in pubsub.js. A const
     never becomes a property of window, so `window.PUB_SUB_EVENTS` is
     undefined while the bare identifier resolves perfectly. Reading it off
     window fails silently, which is exactly how this shipped broken the first
     time and was only caught by running the failure it was written to prevent.
     Measured on the live storefront, 29 Aug 2026. */
  function events() {
    return typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null;
  }

  function text(node) {
    return node ? node.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  /* ---- reading the page's own prices --------------------------------------- */

  function readPrice(scope, sectionId) {
    if (!scope) return null;
    var el = scope.getElementById
      ? scope.getElementById('price-' + sectionId)
      : scope.querySelector('#price-' + (window.CSS && CSS.escape ? CSS.escape(sectionId) : sectionId));
    if (!el) return null;

    var wrap = el.querySelector('.price');
    var onSale = !!(wrap && wrap.classList.contains('price--on-sale'));

    var now = onSale
      ? text(el.querySelector('.price__sale .price-item--sale'))
      : text(el.querySelector('.price__regular .price-item--regular'));

    var was = onSale ? text(el.querySelector('.price__sale s.price-item--regular')) : '';

    if (!now) return null;
    return { now: now, was: was };
  }

  /* ---- money, rendered in whatever shape the page is already using ---------- */

  var SHAPES = [
    { dec: 2, d: '.', t: ',' },
    { dec: 2, d: ',', t: '.' },
    { dec: 2, d: '.', t: ' ' },
    { dec: 2, d: ',', t: ' ' },
    { dec: 2, d: ',', t: ' ' },
    { dec: 2, d: '.', t: "'" },
    { dec: 2, d: '.', t: '' },
    { dec: 2, d: ',', t: '' },
    { dec: 0, d: '', t: ',' },
    { dec: 0, d: '', t: '.' },
    { dec: 0, d: '', t: ' ' },
    { dec: 0, d: '', t: ' ' },
    { dec: 0, d: '', t: '' }
  ];

  function render(cents, shape) {
    var n = Math.abs(Math.round(cents));
    var whole = shape.dec === 2 ? Math.floor(n / 100) : Math.round(n / 100);
    var out = String(whole);
    if (shape.t) out = out.replace(/\B(?=(\d{3})+(?!\d))/g, shape.t);
    if (shape.dec === 2) {
      var frac = String(n % 100);
      while (frac.length < 2) frac = '0' + frac;
      out += shape.d + frac;
    }
    return (cents < 0 ? '-' : '') + out;
  }

  /* Learn the shape from a string the theme rendered, then restate a different
     amount in it. Returns null when nothing reproduces the sample, so the
     caller can leave the price alone. */
  function restate(sampleText, sampleCents, targetCents) {
    if (!sampleText || typeof sampleCents !== 'number') return null;
    var m = String(sampleText).match(/\d[\d.,'  ]*\d|\d/);
    if (!m) return null;
    for (var i = 0; i < SHAPES.length; i++) {
      if (render(sampleCents, SHAPES[i]) === m[0]) {
        return sampleText.replace(m[0], render(targetCents, SHAPES[i]));
      }
    }
    return null;
  }

  /* ---- what the switches add ----------------------------------------------- */

  function addonTotals() {
    var on = document.querySelectorAll('[data-stka-item].is-on');
    if (!on.length) return null;
    var now = 0;
    var was = 0;
    for (var i = 0; i < on.length; i++) {
      var p = parseInt(on[i].getAttribute('data-price-cents'), 10);
      if (isNaN(p)) continue;
      var w = parseInt(on[i].getAttribute('data-was-cents'), 10);
      now += p;
      was += isNaN(w) || w < p ? p : w;
    }
    return now > 0 ? { now: now, was: was } : null;
  }

  /* ---- writing ------------------------------------------------------------- */

  /* ONLY WRITE ON CHANGE. Writing textContent is itself a DOM mutation, and
     sticky-atc.liquid runs a MutationObserver over the product column that
     rebuilds the mobile bar on any change in there. An unconditional write is
     how those turn into loops. */
  function set(node, value, show) {
    if (!node) return;
    if (value != null && node.textContent !== value) node.textContent = value;
    if (node.hasAttribute('hidden') === show) node.toggleAttribute('hidden', !show);
  }

  function attr(el, name, value) {
    if (el.getAttribute(name) !== String(value)) el.setAttribute(name, value);
  }

  function update(button, variant, html) {
    var sectionId = button.getAttribute('data-section-id');
    var priceEl = button.querySelector('[data-stkb-price]');
    var wasEl = button.querySelector('[data-stkb-was]');
    if (!priceEl) return;

    if (variant && typeof variant.price === 'number') {
      attr(button, 'data-base-price', variant.price);
      attr(button, 'data-base-compare', variant.compare_at_price || 0);
    }

    var unavailable = button.hasAttribute('disabled') || (variant && variant.available === false);
    if (unavailable) {
      set(priceEl, null, false);
      set(wasEl, null, false);
      return;
    }

    var base = readPrice(html, sectionId) || readPrice(document, sectionId);
    if (!base) {
      set(priceEl, null, false);
      set(wasEl, null, false);
      if (window.console && console.debug) {
        console.debug('[stk-buybtn] no price element for section', sectionId, 'price hidden');
      }
      return;
    }

    var add = button.getAttribute('data-include-addons') === '1' ? addonTotals() : null;
    if (!add) {
      set(priceEl, base.now, true);
      set(wasEl, base.was, !!base.was);
      return;
    }

    var baseNow = parseInt(button.getAttribute('data-base-price'), 10);
    var baseWas = parseInt(button.getAttribute('data-base-compare'), 10) || 0;
    if (isNaN(baseNow)) {
      set(priceEl, base.now, true);
      set(wasEl, base.was, !!base.was);
      return;
    }

    var nowText = restate(base.now, baseNow, baseNow + add.now);
    if (!nowText) {
      /* Could not learn the money format from the page. Show the product price
         untouched rather than a number in a shape this shop does not use. */
      set(priceEl, base.now, true);
      set(wasEl, base.was, !!base.was);
      return;
    }
    set(priceEl, nowText, true);

    /* The compare-at baseline is the struck price when there is one, and the
       plain price when there is not: a product not on sale can still be part of
       a saving once an add-on with its own compare-at joins it. */
    var wasSample = base.was || base.now;
    var wasBase = base.was ? baseWas : baseNow;
    var wasTotal = wasBase + add.was;
    var nowTotal = baseNow + add.now;
    var wasText = wasTotal > nowTotal ? restate(wasSample, wasBase, wasTotal) : null;
    set(wasEl, wasText, !!wasText);
  }

  function refresh(variant, html, sectionId) {
    var buttons = document.querySelectorAll('[data-stkb]');
    for (var i = 0; i < buttons.length; i++) {
      if (sectionId && buttons[i].getAttribute('data-section-id') !== sectionId) continue;
      update(buttons[i], variant, html);
    }
  }

  function start() {
    var E = events();
    if (typeof subscribe === 'function' && E) {
      subscribe(E.variantChange, function (event) {
        var d = (event && event.data) || event || {};
        refresh(d.variant, d.html, d.sectionId);
      });
    } else if (window.console && console.warn) {
      console.warn('[stk-buybtn] pubsub unavailable, the button price will not follow the variant');
    }

    /* The add-ons block announces itself rather than the two scripts importing
       each other, so either can be absent without the other noticing. */
    document.addEventListener('stk:addons:change', function () {
      refresh(null, null, null);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
